/**
 * Attribution Tracking Utility
 * Captures and manages campaign attribution data (UTM parameters, referrers, etc.)
 * for tracking marketing campaign effectiveness
 */

export interface AttributionData {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  utm_id?: string;
  referrer?: string;
  landing_page?: string;
  timestamp?: string;
  gclid?: string; // Google Ads click ID
  gbraid?: string; // Google Ads click ID (iOS app-to-web)
  wbraid?: string; // Google Ads click ID (iOS web-to-app)
  fbclid?: string; // Facebook click ID
  msclkid?: string; // Microsoft Ads click ID
  oppref?: string; // ChatGPT Ads (OpenAI) click ID
}

const ATTRIBUTION_STORAGE_KEY = 'booking_attribution';

const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id'];
const CLICK_ID_PARAMS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'oppref'];

/**
 * A URL with only its campaign parameters kept. The app opens this page
 * with the logged-in guest's name, phone and email in the query string,
 * and none of that may be stored or sent on as "landing page".
 */
export function stripToCampaignParams(url: string): string {
  try {
    const parsed = new URL(url);
    const kept = new URLSearchParams();
    parsed.searchParams.forEach((value, key) => {
      if (UTM_PARAMS.includes(key) || CLICK_ID_PARAMS.includes(key)) kept.append(key, value);
    });
    const query = kept.toString();
    return `${parsed.origin}${parsed.pathname}${query ? `?${query}` : ''}`;
  } catch {
    return url.split('?')[0].split('#')[0];
  }
}

function hasCampaign(data: Partial<AttributionData> | null): boolean {
  return Boolean(
    data && (data.utm_source || data.utm_campaign || data.utm_id || CLICK_ID_PARAMS.some((key) => data[key])),
  );
}

/**
 * Parse URL query parameters and extract attribution data
 */
function parseAttributionFromUrl(): Partial<AttributionData> {
  if (typeof window === 'undefined') return {};

  const urlParams = new URLSearchParams(window.location.search);
  const attribution: Partial<AttributionData> = {};

  // UTM parameters
  UTM_PARAMS.forEach((param) => {
    const value = urlParams.get(param);
    if (value) {
      attribution[param] = value;
    }
  });

  // Click IDs
  CLICK_ID_PARAMS.forEach((param) => {
    const value = urlParams.get(param);
    if (value) {
      attribution[param] = value;
    }
  });

  // Referrer and landing page, without anything but campaign parameters
  if (document.referrer) {
    attribution.referrer = stripToCampaignParams(document.referrer);
  }
  attribution.landing_page = stripToCampaignParams(window.location.href);

  // Timestamp
  attribution.timestamp = new Date().toISOString();

  return attribution;
}

/**
 * Get stored attribution data from sessionStorage
 */
function getStoredAttribution(): AttributionData | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!stored) return null;

    // sessionStorage only lives as long as the tab, so no expiry is needed.
    return JSON.parse(stored);
  } catch (error) {
    console.error('Error reading attribution data:', error);
    return null;
  }
}

/**
 * Store attribution data in sessionStorage
 */
function storeAttribution(data: AttributionData): void {
  if (typeof window === 'undefined') return;

  try {
    sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error storing attribution data:', error);
  }
}

/**
 * Initialize attribution tracking
 * Should be called on app initialization
 * First touch per tab: a stored campaign is kept, but a stored "direct"
 * record is replaced when the tab later opens a campaign link.
 */
export function initializeAttribution(): AttributionData {
  if (typeof window === 'undefined') {
    return {};
  }

  const existingAttribution = getStoredAttribution();
  const newAttribution = parseAttributionFromUrl();
  const hasAttribution = hasCampaign(newAttribution);

  if (existingAttribution && (hasCampaign(existingAttribution) || !hasAttribution)) {
    return existingAttribution;
  }

  if (hasAttribution) {
    console.log('Captured new attribution:', newAttribution);
    storeAttribution(newAttribution as AttributionData);
    return newAttribution as AttributionData;
  }

  // No attribution data - store basic info
  const basicAttribution: AttributionData = {
    referrer: document.referrer ? stripToCampaignParams(document.referrer) : 'direct',
    landing_page: stripToCampaignParams(window.location.href),
    timestamp: new Date().toISOString(),
  };

  storeAttribution(basicAttribution);
  return basicAttribution;
}

/**
 * Get current attribution data
 * Returns null if no attribution has been captured
 */
export function getAttribution(): AttributionData | null {
  return getStoredAttribution();
}

/**
 * Clear attribution data (useful for testing)
 */
export function clearAttribution(): void {
  if (typeof window === 'undefined') return;

  try {
    sessionStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
    console.log('Attribution data cleared');
  } catch (error) {
    console.error('Error clearing attribution data:', error);
  }
}

/**
 * Get formatted attribution string for logging
 * Example: "Google Ads (my-campaign)"
 */
export function getAttributionSummary(attribution: AttributionData | null): string {
  if (!attribution) return 'Unknown';

  const parts: string[] = [];

  if (attribution.utm_source) {
    parts.push(attribution.utm_source);
  }

  if (attribution.utm_medium) {
    parts.push(`(${attribution.utm_medium})`);
  }

  if (attribution.utm_campaign) {
    parts.push(`- ${attribution.utm_campaign}`);
  }

  if (attribution.gclid) {
    parts.push('[Google Ads]');
  }

  if (attribution.fbclid) {
    parts.push('[Facebook Ads]');
  }

  return parts.length > 0 ? parts.join(' ') : attribution.referrer || 'Direct';
}
